import { useCallback, useMemo } from "react";
import useSWRInfinite from "swr/infinite";
import { AgentMessage } from "@/models/agent/message";
import { AgentService } from "@/services/agent/agent.service";
import { defaultSWROption } from "@/services/api/api.service";

const PAGE_SIZE = 40;

export const THREAD_MESSAGES_SWR_KEY = "thread-messages-infinite";

const fetcher = async ([, threadId, offset]: [string, string, number]) => {
  const { data } = await AgentService.getThreadMessages(threadId, offset, PAGE_SIZE);
  return data ?? [];
};

export const createThreadMessagesInfiniteKeyLoader =
  (threadId?: string) => (pageIndex: number, previousPageData: AgentMessage[] | null) => {
    if (!threadId) return null;
    if (previousPageData && previousPageData.length < PAGE_SIZE) return null;
    const offset = pageIndex * PAGE_SIZE;
    return [THREAD_MESSAGES_SWR_KEY, threadId, offset] as const;
  };

export const useThreadMessagesInfinite = (threadId?: string) => {
  const getKey = createThreadMessagesInfiniteKeyLoader(threadId);

  const {
    data: pages,
    error,
    isLoading,
    isValidating,
    size,
    setSize,
    mutate,
  } = useSWRInfinite(getKey, fetcher, {
    ...defaultSWROption,
    revalidateFirstPage: false,
    revalidateAll: false,
    parallel: false,
    revalidateOnMount: true,
    dedupingInterval: 0,
  });

  // 全ページのメッセージをフラット化（新しいメッセージが前）、サブエージェント応答を除外
  const messages = useMemo(
    () => (pages?.flat() ?? []).filter((msg) => !msg.isSubAgentFinalResponse),
    [pages],
  );

  const lastPageLength = pages?.[pages.length - 1]?.length;
  const hasMore = !error && (lastPageLength ?? PAGE_SIZE) >= PAGE_SIZE;
  const isLoadingMore = isValidating && size > 1 && pages?.[size - 1] === undefined;

  const loadMore = useCallback(() => {
    if (!isValidating && hasMore) {
      setSize((s) => s + 1);
    }
  }, [isValidating, hasMore, setSize]);

  return {
    messages,
    isLoading,
    isLoadingMore: !!isLoadingMore,
    hasMore,
    loadMore,
    mutate,
    error,
  };
};
